import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '@/state/ui.store'

const APP_RULES = {
  chatbot: { plans: ['Growth', 'Agency'], lifetime: true, modalCase: 'chatbot' },
  // cookieBanner was ungated product-wide (cookie-manager feature) — no rule.
  calendar: { plans: ['Growth', 'Agency'], lifetime: true, modalCase: 'calendar' },
  emailAutomation: { plans: ['Growth', 'Agency'], lifetime: true, modalCase: 'emailAutomation' },
  formBuilder: { plans: ['Growth', 'Agency'], lifetime: true, modalCase: 'formBuilder' },
  // Website Canvas apps — same plan tier as the other nocode apps.
  forms: { plans: ['Growth', 'Agency'], lifetime: true, modalCase: null },
  blog: { plans: ['Growth', 'Agency'], lifetime: true, modalCase: null },
}

export function hasNocodeAppEntitlement(workspace, appKey) {
  const rule = APP_RULES[appKey]
  if (!rule) return false
  const plans = getUsableWorkspacePlans(workspace)

  return plans.some((plan) => (
    rule.plans.includes(plan?.planType) ||
    (rule.lifetime && isLifetimePlan(plan))
  ))
}

export function getUsableWorkspacePlans(workspace) {
  const plans = Array.isArray(workspace?.activePlans)
    ? workspace.activePlans
    : (workspace?.plans || []).filter((plan) => plan?.isActive)

  return plans.filter(isUsablePlan)
}

export function hasPaidWorkspacePlan(workspace) {
  return getUsableWorkspacePlans(workspace).length > 0
}

export function useNocodeAppGate(workspace, appKey) {
  const navigate = useNavigate()
  const setShowPricingModal = useUIStore((state) => state.setShowPricingModal)
  const showPricingModal = useUIStore((state) => state.showPricingModal)
  const openedPricingRef = useRef(false)
  const allowed = hasNocodeAppEntitlement(workspace, appKey)
  const hasWorkspace = Boolean(workspace)
  const modalCase = APP_RULES[appKey]?.modalCase || appKey

  useEffect(() => {
    if (!hasWorkspace || allowed) {
      openedPricingRef.current = false
      return
    }
    openedPricingRef.current = true
    setShowPricingModal(true, modalCase)
  }, [allowed, hasWorkspace, modalCase, setShowPricingModal])

  useEffect(() => {
    if (!hasWorkspace || allowed) return
    if (!openedPricingRef.current || showPricingModal) return
    openedPricingRef.current = false
    navigate('/', { replace: true })
  }, [allowed, hasWorkspace, navigate, showPricingModal])

  return { allowed, hasWorkspace }
}

function isUsablePlan(plan) {
  const name = String(plan?.planName || '').toLowerCase()
  return Boolean(
    plan &&
    plan.isActive === true &&
    plan.isCancelled !== true &&
    plan.hasRefunded !== true &&
    !name.includes('domain purchase'),
  )
}

function isLifetimePlan(plan) {
  return Boolean(
    plan?.lifetime === true ||
    plan?.planDuration === 'lifetime' ||
    plan?.planType === 'Lifetime',
  )
}
